import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    IconButton,
    Autocomplete,
    Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { updateLane, getCompetitionTeams, getCompetitionCompetitors, getCompetitionJudges } from '../api/competitionApi';

export default function EditLaneDialog({ open, onClose, lane, heat, schedule, isTeamCompetition }) {
    const [formData, setFormData] = useState({
        time: '',
        registrationId: null,
        selectedOption: null,
        judgeId: null,
        selectedJudge: null
    });
    const [competitors, setCompetitors] = useState([]);
    const [teams, setTeams] = useState([]);
    const [judges, setJudges] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (lane) {
            setFormData({
                time: lane.time || '',
                registrationId: lane.registrationId || null,
                selectedOption: null,
                judgeId: lane.judgeId || null,
                selectedJudge: null
            });
        } else {
            setFormData({ time: '', registrationId: null, selectedOption: null, judgeId: null, selectedJudge: null });
        }
    }, [lane, open]);

    // Set selectedOption when teams/competitors are loaded and lane has registrationId
    useEffect(() => {
        if (lane?.registrationId && open) {
            if (isTeamCompetition && teams.length > 0) {
                const selectedTeam = teams.find(t => t.id === lane.registrationId);
                if (selectedTeam) {
                    setFormData(prev => ({ ...prev, selectedOption: selectedTeam }));
                }
            } else if (!isTeamCompetition && competitors.length > 0) {
                const selectedCompetitor = competitors.find(c => c.id === lane.registrationId);
                if (selectedCompetitor) {
                    setFormData(prev => ({ ...prev, selectedOption: selectedCompetitor }));
                }
            }
        }
    }, [lane?.registrationId, teams, competitors, isTeamCompetition, open]);

    // Load teams/competitors and judges when dialog opens
    useEffect(() => {
        const loadData = async () => {
            if (open && lane?.competitionId) {
                try {
                    if (isTeamCompetition) {
                        const teamsData = await getCompetitionTeams(lane.competitionId);
                        setTeams(teamsData);
                    } else {
                        const competitorsData = await getCompetitionCompetitors(lane.competitionId);
                        setCompetitors(competitorsData);
                    }
                    // Load judges
                    const judgesData = await getCompetitionJudges(lane.competitionId);
                    setJudges(judgesData);
                } catch (error) {
                    console.error('Error loading data:', error);
                }
            }
        };
        loadData();
    }, [open, isTeamCompetition, lane?.competitionId]);

    // Set selectedJudge when judges are loaded and lane has judgeId
    useEffect(() => {
        if (lane?.judgeId && open && judges.length > 0) {
            const selectedJudge = judges.find(j => j.id === lane.judgeId);
            if (selectedJudge) {
                setFormData(prev => ({ ...prev, selectedJudge }));
            }
        }
    }, [lane?.judgeId, judges, open]);

    // Get registrationIds already assigned in this schedule (excluding current lane)
    const getAssignedRegistrationIds = () => {
        if (!schedule || !schedule.heats) return [];
        const assignedIds = [];
        schedule.heats.forEach(heatItem => {
            if (heatItem.lanes) {
                heatItem.lanes.forEach(l => {
                    if (l.id !== lane?.id && l.registrationId !== null) {
                        assignedIds.push(l.registrationId);
                    }
                });
            }
        });
        return assignedIds;
    };

    // Filter teams/competitors to exclude those already assigned in the heat
    // But always include the currently assigned one (if any)
    const getAvailableTeams = () => {
        const assignedIds = getAssignedRegistrationIds();
        return teams.filter(team => 
            !assignedIds.includes(team.id) || team.id === lane?.registrationId
        );
    };

    const getAvailableCompetitors = () => {
        const assignedIds = getAssignedRegistrationIds();
        return competitors.filter(competitor => 
            !assignedIds.includes(competitor.id) || competitor.id === lane?.registrationId
        );
    };

    // Get judgeIds already assigned in this heat (excluding current lane)
    const getAssignedJudgeIds = () => {
        if (!heat || !heat.lanes) return [];
        const assignedIds = [];
        heat.lanes.forEach(l => {
            if (l.id !== lane?.id && l.judgeId !== null) {
                assignedIds.push(l.judgeId);
            }
        });
        return assignedIds;
    };

    // Filter judges to exclude those already assigned in the heat
    // But always include the currently assigned one (if any)
    const getAvailableJudges = () => {
        const assignedIds = getAssignedJudgeIds();
        return judges.filter(judge => 
            !assignedIds.includes(judge.id) || judge.id === lane?.judgeId
        );
    };

    const handleSubmit = async () => {
        if (!lane) return;

        try {
            setLoading(true);
            setErrorMessage('');
            await updateLane(lane.id, {
                time: formData.time || null,
                registrationId: formData.registrationId || null,
                judgeId: formData.judgeId || null
            });
            onClose();
        } catch (error) {
            console.error('Error updating lane:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to update lane');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = async () => {
        if (!lane) return;

        try {
            setLoading(true);
            setErrorMessage('');
            await updateLane(lane.id, {
                time: null,
                registrationId: null,
                judgeId: null
            });
            onClose();
        } catch (error) {
            console.error('Error clearing lane:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to clear lane');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Edit Lane {lane?.laneNumber}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Time Slot"
                            value={formData.time}
                            onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                            placeholder="e.g., 09:15-09:20, 10:00, Morning Session"
                            helperText="Enter any text to indicate the time slot for this lane"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        {isTeamCompetition ? (
                            // Team Competition - Show all teams in dropdown (excluding already assigned)
                            <Autocomplete
                                options={getAvailableTeams()}
                                getOptionLabel={(option) =>
                                    `${option.teamName} (${option.category})`
                                }
                                value={formData.selectedOption}
                                onChange={(e, value) => setFormData(prev => ({
                                    ...prev,
                                    registrationId: value?.id || null,
                                    selectedOption: value
                                }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Assign Team"
                                        helperText="Select a registered team (excluding already assigned in this schedule)"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="500">
                                                {option.teamName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.affiliate ? `${option.affiliate} • ` : ''}{option.category}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            />
                        ) : (
                            // Individual Competition - Show all competitors in dropdown (excluding already assigned)
                            <Autocomplete
                                options={getAvailableCompetitors()}
                                getOptionLabel={(option) =>
                                    `${option.fullName}`
                                }
                                value={formData.selectedOption}
                                onChange={(e, value) => setFormData(prev => ({
                                    ...prev,
                                    registrationId: value?.id || null,
                                    selectedOption: value
                                }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Assign Competitor"
                                        placeholder="Select a competitor..."
                                        helperText="Select a registered competitor (excluding already assigned in this schedule)"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="500">
                                                {option.fullName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.affiliate ? `${option.affiliate} • ` : ''}{option.category}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            options={getAvailableJudges()}
                            getOptionLabel={(option) =>
                                `${option.user?.fullName || 'Unknown'} (${option.user?.email || ''})`
                            }
                            value={formData.selectedJudge}
                            onChange={(e, value) => setFormData(prev => ({
                                ...prev,
                                judgeId: value?.id || null,
                                selectedJudge: value
                            }))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Assign Judge"
                                    placeholder="Select a judge..."
                                    helperText="Select a judge (excluding already assigned in this heat)"
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="500">
                                            {option.user?.fullName || 'Unknown'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {option.user?.email || ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        />
                    </Grid>

                    {lane?.competitorName && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: 'info.light',
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="body2" color="info.dark" gutterBottom>
                                    <strong>Current Assignment:</strong>
                                </Typography>
                                <Typography variant="body2" color="info.dark">
                                    {lane.competitorName}

                                    {lane.division && ` (${lane.division})`}
                                </Typography>
                                {lane.time && (
                                    <Typography variant="body2" color="info.dark">
                                        Time: {lane.time}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                {(lane?.competitorName || lane?.time) && (
                    <Button
                        onClick={handleClear}
                        disabled={loading}
                        color="error"
                        sx={{ mr: 'auto' }}
                    >
                        Clear Lane
                    </Button>
                )}
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}